import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Card, Title, Text, Badge,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
  Button,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchArchives, downloadArchive } from '../api/archives';
import AccessDenied from '../components/AccessDenied';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import type { ArchiveListing, User } from '../api/types';
import Header from '../components/Header';
import { t } from '../lib/i18n';

const fmtBytes = (n: number) =>
  n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB`
  : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB`
  : n >= 1e3 ? `${(n / 1e3).toFixed(1)} KB`
  : `${n} B`;

const fmtDate = (s: string) =>
  s ? new Date(s).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : t.archives.never;

export default function Archives() {
  // Tri-state currentUser load: undefined = pending; null = redirect; User = render.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  return <ArchivesInner user={user} />;
}

function ArchivesInner({ user }: { user: User }) {
  const [data, setData] = useState<ArchiveListing | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    if (user.role !== 'owner') return;
    let alive = true;
    setErr(null);
    fetchArchives()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => {
        if (!alive) return;
        setErr(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  };
  useEffect(() => load(), [user.role]);

  // /archives is owner-only — accounting/manager/staff all get 403. The Go
  // handler enforces the same rule; this nav-side guard is a UX nicety so
  // non-owners who guess the URL see a friendly placeholder rather than the
  // raw HTTP error.
  if (user.role !== 'owner') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} requiredRoleLabel={t.roles.owner} /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.archives.title}</Title>
        <Text className="text-tremor-content text-sm">{t.archives.subtitle}</Text>
        {data && (
          <Text className="text-tremor-content text-xs">
            {t.archives.lastRun}: {fmtDate(data.last_run_at)}
          </Text>
        )}
        {err && <ErrorRetry message={err} onRetry={load} />}
        {!data && !err && <TableSkeleton rows={4} cols={4} />}
        {data?.streams.map((s) => (
          <Card key={s.name}>
            <div className="flex items-baseline gap-3">
              <Title>{s.name}</Title>
              {s.ftp_enabled && (
                s.ftp_last_push_ok
                  ? <Badge color="green">{t.archives.ftpBackedUp}</Badge>
                  : <Badge color="red">{t.archives.ftpFailed}: {s.ftp_last_error ?? '—'}</Badge>
              )}
            </div>
            {s.files.length === 0 ? (
              <Text className="mt-2">{t.archives.empty}</Text>
            ) : (
              <Table className="mt-3">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.archives.cols.filename}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.archives.cols.size}</TableHeaderCell>
                    <TableHeaderCell>{t.archives.cols.archivedAt}</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.files.map((f) => (
                    <TableRow key={f.filename}>
                      <TableCell className="font-mono text-xs">{f.filename}</TableCell>
                      <TableCell className="text-right">{fmtBytes(f.size_bytes)}</TableCell>
                      <TableCell>{fmtDate(f.archived_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="xs"
                          variant="secondary"
                          onClick={() => downloadArchive(s.name, f.filename)}
                        >
                          {t.archives.download}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        ))}
      </main>
    </div>
  );
}
